import { create } from 'zustand'
import {
  collection,
  doc,
  getDocs,
  setDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore'
import { db } from '../config/firebase'
import {
  CareerCategoryConfig,
  CareerCategoryUpdate,
  defaultCareerCategories,
} from '../models/CareerCategory'

const COLLECTION = 'nursing_career_categories'

interface CareerCategoryState {
  categories: CareerCategoryConfig[]
  loading: boolean
  error: string | null

  fetchCategories: () => Promise<void>
  updateCategory: (number: number, data: CareerCategoryUpdate) => Promise<void>
  initializeDefaults: () => Promise<void>
}

export const useCareerCategoryStore = create<CareerCategoryState>((set, get) => ({
  categories: [],
  loading: false,
  error: null,

  fetchCategories: async () => {
    set({ loading: true, error: null })
    try {
      const snap = await getDocs(collection(db, COLLECTION))
      const fromFirestore: Record<number, CareerCategoryConfig> = {}
      snap.forEach((d) => {
        const num = parseInt(d.id, 10)
        if (!isNaN(num)) {
          const data = d.data()
          fromFirestore[num] = {
            number: num,
            categoryLabel: defaultCareerCategories.find((c) => c.number === num)?.categoryLabel ?? '',
            description: data.description ?? '',
            nursingJobs: data.nursingJobs ?? [],
            updatedAt: data.updatedAt?.toDate(),
          }
        }
      })

      // 合併：Firestore 有資料用 Firestore，否則用預設值
      const merged = defaultCareerCategories.map((def) =>
        fromFirestore[def.number]
          ? fromFirestore[def.number]
          : { ...def }
      )
      set({ categories: merged, loading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '載入失敗'
      set({ loading: false, error: msg })
    }
  },

  updateCategory: async (number, data) => {
    set({ loading: true, error: null })
    try {
      await setDoc(
        doc(db, COLLECTION, String(number)),
        { ...data, updatedAt: serverTimestamp() },
        { merge: true }
      )
      await get().fetchCategories()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '更新失敗'
      set({ loading: false, error: msg })
      throw e
    }
  },

  initializeDefaults: async () => {
    set({ loading: true, error: null })
    try {
      const batch = writeBatch(db)
      for (const cat of defaultCareerCategories) {
        const ref = doc(db, COLLECTION, String(cat.number))
        batch.set(ref, {
          description: cat.description,
          nursingJobs: cat.nursingJobs,
          updatedAt: serverTimestamp(),
        })
      }
      await batch.commit()
      await get().fetchCategories()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '初始化失敗'
      set({ loading: false, error: msg })
      throw e
    }
  },
}))
